import {
  ICustomChartSettings,
  ICustomReportChart,
  ICustomReportColumn,
  ICustomReportDefinedColumn,
  ICustomReportFilter,
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  IAccessorContext,
  IChartFilter,
  IColumnSection,
  IGroupBySection,
  IQueryableTimestamp,
  IReportBuilderDataSource,
  IReportingQuery,
  ScopeDataAccessorMap,
} from '@principle-theorem/reporting/interfaces';
import { compact, first } from 'lodash';
import { BaseMeasures } from '../models/base-measures';
import { QueryFactory } from '../models/query-factory';
import { ReportBuilderQueryBuilder } from '../models/report-builder-query-builder';
import { ReportBuilderDataSource } from './report-builder-data-source';

export abstract class ReportBuilderDataSourceInstance
  implements IReportBuilderDataSource
{
  abstract readonly id: ReportBuilderDataSourceId;
  abstract readonly name: string;
  abstract readonly description: string;
  abstract readonly factTable: BaseMeasures;
  abstract readonly scopes: TemplateScope[];
  abstract readonly scopeDataAccessor: ScopeDataAccessorMap;
  abstract readonly rowItemName: string;
  readonly isRetired: boolean = false;
  readonly queryableTimestamps: IQueryableTimestamp[] = [];
  readonly groupByOptions: IGroupBySection[] = [];
  readonly availableColumns: IColumnSection[] = [];
  readonly staticFilters: ICustomReportFilter[] = [];
  readonly defaultColumns: string[] = [];
  readonly availableCharts: IChartFilter<ICustomChartSettings>[] = [];

  allColumns(): ICustomReportDefinedColumn[] {
    return this.availableColumns.map((section) => section.columns).flat();
  }

  findColumn(columnId: string): ICustomReportDefinedColumn | undefined {
    return this.allColumns().find((column) => column.id === columnId);
  }

  getDefaultColumns(): ICustomReportDefinedColumn[] {
    const defaultColumns = this.defaultColumns.map((column) =>
      this.findColumn(column)
    );
    return compact(defaultColumns);
  }

  getDefaultQueryableTimestamp(): IQueryableTimestamp {
    const queryableTimestamp = first(this.queryableTimestamps);
    if (!queryableTimestamp) {
      throw new Error(
        `No queryable timestamp defined for data source: ${this.id}`
      );
    }
    return queryableTimestamp;
  }

  buildQuery(
    columns: ICustomReportColumn[],
    charts: ICustomReportChart[],
    filters: ICustomReportFilter[],
    context: IAccessorContext
  ): IReportingQuery {
    const query = QueryFactory.fromTable(this.factTable.table)
      .merge([
        this.factTable.query,
        ...ReportBuilderQueryBuilder.getFilterQueries(this, filters, context),
        ...ReportBuilderQueryBuilder.getTableQueries(this, columns),
        ...ReportBuilderQueryBuilder.getChartQueries(this, charts),
      ])
      .get();

    if (!query) {
      throw new Error(
        `Reporting query not implemented for data source: ${this.id}`
      );
    }
    return query;
  }

  getQueryColumns(
    customColumns?: ICustomReportColumn[]
  ): ICustomReportDefinedColumn[] {
    const allColumns = ReportBuilderDataSource.allColumns(this);
    if (!customColumns) {
      return allColumns;
    }
    const columnIds = customColumns.map((column) => column.id);
    return allColumns.filter((column) => columnIds.includes(column.id));
  }

  getQueryCharts(customCharts?: ICustomReportChart[]): ICustomReportChart[] {
    if (!customCharts) {
      return this.availableCharts
        .filter((chart) => !!chart.availableInEditMode)
        .map((chart) => chart.chart);
    }
    return customCharts;
  }
}
